import { LeaveType, LeaveTypeCode, LeaveUnit } from '../types/leaveType';
import { LeaveBalance } from '../types/leaveBalance';
import { LeaveRequestInput } from '../types/leaveRequest';
import { getQuarterFromDate } from '../types/personalHealingDay';
import { DEFAULT_ALLOWED_UNITS } from '../constants/leaveUnits';  // V282-1

/**
 * 휴가 신청 검증 결과
 */
export interface LeaveValidationResult {
  /** 검증 통과 여부 */
  isValid: boolean;

  /** 에러 메시지 (검증 실패 시) */
  errorMessage?: string;

  /** 계산된 총 일수 */
  calculatedDays?: number;

  /** 차감될 일수 (음수 가능) */
  deductionDays?: number;
}

/**
 * 휴가 신청 검증
 *
 * @param input 휴가 신청 입력
 * @param leaveType 휴가 유형
 * @param balance 휴가 잔액
 * @returns 검증 결과
 */
export function validateLeaveRequest(
  input: LeaveRequestInput,
  leaveType: LeaveType,
  balance: LeaveBalance,
  /** V61: CN 정책 분기용 */
  companyCode?: string
): LeaveValidationResult {
  // 1. 필수 필드 검증
  if (!input.startDate || !input.endDate) {
    return {
      isValid: false,
      errorMessage: '시작일과 종료일은 필수입니다.',
    };
  }

  // V273: 연도 4자리 제한 (YYYY-MM-DD 형식 검증)
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(input.startDate) || !datePattern.test(input.endDate)) {
    return {
      isValid: false,
      errorMessage: '날짜는 YYYY-MM-DD 형식(연도 4자리)이어야 합니다.',
    };
  }

  // 2. 날짜 유효성 검증
  const startDate = new Date(input.startDate);
  const endDate = new Date(input.endDate);

  if (startDate > endDate) {
    return {
      isValid: false,
      errorMessage: '시작일은 종료일보다 이전이어야 합니다.',
    };
  }

  // 3. 사용 단위 검증 (V282-1: 단일 정책 상수 사용)
  const effectiveUnits = leaveType.allowedUnits?.length ? leaveType.allowedUnits : DEFAULT_ALLOWED_UNITS;
  if (!effectiveUnits.includes(input.unit)) {
    return {
      isValid: false,
      errorMessage: `${leaveType.name}은(는) ${input.unit} 단위로 사용할 수 없습니다.`,
    };
  }

  // 4. 첨부파일 검증 (V70: 독립 검증)
  if (leaveType.requiresAttachment) {
    const hasAttachment = input.metadata?.attachments && input.metadata.attachments.length > 0;
    if (!hasAttachment) {
      return {
        isValid: false,
        errorMessage: `${leaveType.name}은(는) 첨부파일이 필수입니다.`,
      };
    }
  }

  // 4-2. 사유 검증 (V70: 독립 검증)
  if (leaveType.requiresReason) {
    const hasReason = input.metadata?.reason && input.metadata.reason.trim().length > 0;
    if (!hasReason) {
      return {
        isValid: false,
        errorMessage: `${leaveType.name}은(는) 사유 입력이 필수입니다.`,
      };
    }
  }

  // 5. 총 일수 계산
  const calculatedDays = calculateLeaveDays(input.startDate, input.endDate, input.unit);

  // 6. 퍼스널힐링데이 특수 규칙 검증
  if (leaveType.code === 'personalHealing') {
    const quarter = getQuarterFromDate(input.startDate);
    const quarterUsed = getQuarterUsedStatus(balance, quarter);

    if (quarterUsed) {
      return {
        isValid: false,
        errorMessage: `${quarter}분기 퍼스널힐링데이는 이미 사용되었습니다.`,
      };
    }

    // 퍼스널힐링데이는 오후반차만 가능
    if (input.unit !== 'afternoonHalf') {
      return {
        isValid: false,
        errorMessage: '퍼스널힐링데이는 오후반차만 사용 가능합니다.',
      };
    }

    return {
      isValid: true,
      calculatedDays,
      deductionDays: 0, // 차감 없음
    };
  }

  // 7. 잔액 검증
  const deductionDays = calculateDeductionDays(leaveType.code, calculatedDays);

  if (leaveType.deductionType === 'annual') {
    if (balance.annual.remaining < deductionDays) {
      return {
        isValid: false,
        errorMessage: `연차 잔액이 부족합니다. (잔액: ${balance.annual.remaining}일, 필요: ${deductionDays}일)`,
      };
    }
  } else if (leaveType.deductionType === 'compensatory') {
    if (balance.compensatory.remaining < deductionDays) {
      // V61: CN은 대체휴무 보유분 초과 사용 불가 (엄격)
      if (companyCode === 'CN') {
        return {
          isValid: false,
          errorMessage: `대체휴무 잔액이 부족합니다. (잔액: ${balance.compensatory.remaining}일, 필요: ${deductionDays}일)`,
        };
      }
      // KR: 잔액 부족해도 승인자 판단에 따라 사용 가능
      console.warn(
        `대체휴무 잔액 부족 (잔액: ${balance.compensatory.remaining}일, 필요: ${deductionDays}일). 승인자 승인 시 사용 가능.`
      );
    }
  }

  return {
    isValid: true,
    calculatedDays,
    deductionDays,
  };
}

/**
 * 휴가 일수 계산
 *
 * @param startDate 시작일 (YYYY-MM-DD)
 * @param endDate 종료일 (YYYY-MM-DD)
 * @param unit 사용 단위
 * @returns 총 일수
 */
export function calculateLeaveDays(
  startDate: string,
  endDate: string,
  unit: LeaveUnit
): number {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const diffTime = end.getTime() - start.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24) + 1; // +1 to include both start and end

  if (unit === 'fullDay') {
    return diffDays;
  } else if (unit === 'halfDay' || unit === 'morningHalf' || unit === 'afternoonHalf') {
    // V60: 반차 다일 선택 시 각 날짜당 0.5일 (주말 미제외 — FE 간이 계산)
    return diffDays * 0.5;
  }

  return diffDays;
}

/**
 * 차감 일수 계산
 *
 * @param leaveTypeCode 휴가 유형 코드
 * @param calculatedDays 계산된 총 일수
 * @returns 차감 일수
 */
export function calculateDeductionDays(
  leaveTypeCode: LeaveTypeCode,
  calculatedDays: number
): number {
  switch (leaveTypeCode) {
    case 'annual':
      return calculatedDays; // 전일: -1

    case 'halfDayMorning':
    case 'halfDayAfternoon':
      return 0.5; // 반차: -0.5

    case 'compensatory':
      return calculatedDays; // 전일: -1, 반차: -0.5

    case 'personalHealing':
      return 0; // 차감 없음

    default:
      return 0; // 기타 휴가는 차감 없음
  }
}

/**
 * 분기별 퍼스널힐링데이 사용 여부 확인
 *
 * @param balance 휴가 잔액
 * @param quarter 분기 (1~4)
 * @returns 사용 여부
 */
function getQuarterUsedStatus(balance: LeaveBalance, quarter: number): boolean {
  switch (quarter) {
    case 1:
      return balance.personalHealing.q1Used;
    case 2:
      return balance.personalHealing.q2Used;
    case 3:
      return balance.personalHealing.q3Used;
    case 4:
      return balance.personalHealing.q4Used;
    default:
      return false;
  }
}

/**
 * 휴가 잔액 차감
 *
 * @param balance 현재 잔액
 * @param leaveType 휴가 유형
 * @param deductionDays 차감 일수
 * @returns 업데이트된 잔액
 */
export function deductLeaveBalance(
  balance: LeaveBalance,
  leaveType: LeaveType,
  deductionDays: number,
  quarter?: number
): Partial<LeaveBalance> {
  const updated: Partial<LeaveBalance> = {};

  if (leaveType.deductionType === 'annual') {
    updated.annual = {
      ...balance.annual,
      used: balance.annual.used + deductionDays,
      remaining: balance.annual.remaining - deductionDays,
    };
  } else if (leaveType.deductionType === 'compensatory') {
    const newRemaining = Math.max(0, balance.compensatory.remaining - deductionDays);

    updated.compensatory = {
      ...balance.compensatory,
      used: balance.compensatory.used + deductionDays,
      remaining: newRemaining,
    };
  }

  // 퍼스널힐링데이
  if (leaveType.code === 'personalHealing' && quarter) {
    updated.personalHealing = {
      ...balance.personalHealing,
    };

    switch (quarter) {
      case 1:
        updated.personalHealing.q1Used = true;
        break;
      case 2:
        updated.personalHealing.q2Used = true;
        break;
      case 3:
        updated.personalHealing.q3Used = true;
        break;
      case 4:
        updated.personalHealing.q4Used = true;
        break;
    }
  }

  return updated;
}

/**
 * 휴가 잔액 복구 (취소 시)
 *
 * @param balance 현재 잔액
 * @param leaveType 휴가 유형
 * @param deductionDays 복구 일수
 * @returns 업데이트된 잔액
 */
export function restoreLeaveBalance(
  balance: LeaveBalance,
  leaveType: LeaveType,
  deductionDays: number,
  quarter?: number
): Partial<LeaveBalance> {
  const updated: Partial<LeaveBalance> = {};

  if (leaveType.deductionType === 'annual') {
    updated.annual = {
      ...balance.annual,
      used: balance.annual.used - deductionDays,
      remaining: balance.annual.remaining + deductionDays,
    };
  } else if (leaveType.deductionType === 'compensatory') {
    updated.compensatory = {
      ...balance.compensatory,
      used: balance.compensatory.used - deductionDays,
      remaining: balance.compensatory.remaining + deductionDays,
    };
  }

  // 퍼스널힐링데이 복구
  if (leaveType.code === 'personalHealing' && quarter) {
    updated.personalHealing = {
      ...balance.personalHealing,
    };

    switch (quarter) {
      case 1:
        updated.personalHealing.q1Used = false;
        break;
      case 2:
        updated.personalHealing.q2Used = false;
        break;
      case 3:
        updated.personalHealing.q3Used = false;
        break;
      case 4:
        updated.personalHealing.q4Used = false;
        break;
    }
  }

  return updated;
}
